import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Alert,
  ScrollView,
  Animated,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../hooks/useTheme';
import { usePaletteGeneration } from '../../hooks/usePaletteGeneration';
import ColorPicker from './ColorPicker';
import { getRecentAnalysis } from '../../api/colours';
import { usersAPI } from '../../api/users';
import { RootStackParamList } from '../../types/navigation';
import { navigate } from '../../navigation/NavigationService';
import { trackButtonClick } from '../../utils/analytics';
import { authKeys } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import BlurredLockOverlay from './BlurredLockOverlay';
import CreatePalettesCard from './CreatePalettesCard';
import PaletteResult from './PaletteResult';
import SearchBar from './SearchBar';
import SelectedColorsStrip from './SelectedColorsStrip';
import PageHero from './PageHero';
import { getPublicPreviewUsed } from '../../utils/publicPreviewStorage';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface ColorGeneratorProps {
  onClose?: () => void;
  hideUseMyPalette?: boolean;
  onTabPress?: (tab: string) => void;
  /** When true, shows blurred palettes beyond the first with lock overlay and CTA */
  isPublicPreview?: boolean;
}

const ColorGenerator: React.FC<ColorGeneratorProps> = ({
  onClose,
  hideUseMyPalette = false,
  onTabPress,
  isPublicPreview = false,
}) => {
  const { colors } = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();
  const { isProUser, isPlusUser } = useSubscription();
  const paletteResultsRef = useRef<View>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [generatedPalette, setGeneratedPalette] = useState<string[]>([]);
  const [colorPalette, setColorPalette] = useState<string[]>([]);
  const [multipleCombinations, setMultipleCombinations] = useState<
    { name?: string; colors: string[] }[]
  >([]);
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<
    number | null
  >(null);
  const [, setRandomPaletteName] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastAnim = useState(new Animated.Value(100))[0];
  const [recommendedColors, setRecommendedColors] = useState<string[]>([]);
  const [, setLoadingRecommended] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width
  );
  const [hasUsedPublicTry, setHasUsedPublicTry] = useState<boolean | null>(
    null
  );
  const [generatorSearchQuery, setGeneratorSearchQuery] = useState('');

  const {
    generationCount,
    setGenerationCount,
    combinationCount,
    setCombinationCount,
    harmonyMode,
    setHarmonyMode,
    isGenerating,
    setIsGenerating,
    generateFromSelection,
    generateRandom,
  } = usePaletteGeneration();

  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  const isTablet =
    Platform.OS === 'web' && screenWidth >= 768 && screenWidth < 1024;

  // Plan-based limits: Free (5 colors, 3 combos), Plus/Pro (8 colors, 6 combos, export, Pro = harmony)
  const generationCountMax = isPlusUser || isProUser ? 8 : 5;
  const combinationCountMax = isPlusUser || isProUser ? 6 : 3;
  const maxSelection = isPlusUser || isProUser ? 15 : 10;
  const canExport = isPlusUser || isProUser;
  const canUseHarmony = isProUser;

  // Note: Document title is now handled by React Navigation's documentTitle formatter
  // No need for manual title setting here

  // Calculate bottom spacing dynamically based on bottom navigation height and content
  // Bottom navigation: height: 80 + insets.bottom, paddingBottom: insets.bottom + 10
  const bottomNavigationHeight = useMemo(() => {
    if (!isMobile) return 0;
    return 80 + insets.bottom + 10;
  }, [isMobile, insets.bottom]);

  const hasPaletteContent = useMemo(() => {
    return (
      multipleCombinations.length > 0 ||
      generatedPalette.length > 0 ||
      colorPalette.length > 0
    );
  }, [
    multipleCombinations.length,
    generatedPalette.length,
    colorPalette.length,
  ]);

  const scrollViewBottomPadding = useMemo(() => {
    if (!isMobile) return 0;
    const baseBuffer = hasPaletteContent ? 20 : 40;
    return bottomNavigationHeight + baseBuffer;
  }, [isMobile, bottomNavigationHeight, hasPaletteContent]);

  // Load public preview usage on mount
  useEffect(() => {
    if (!isPublicPreview) return;
    void getPublicPreviewUsed('color_generator').then(setHasUsedPublicTry);
  }, [isPublicPreview]);

  const publicPreviewTryUsed = isPublicPreview ? hasUsedPublicTry : null;

  // When user changes "Generate X colors", set "Try X combos" to max
  useEffect(() => {
    setCombinationCount(combinationCountMax.toString());
  }, [generationCount, combinationCountMax, setCombinationCount]);

  // When selected colors change, set "Generate X colors" to max allowed and set combos to max
  useEffect(() => {
    const n = selectedColors.length;
    const maxGen =
      n === 0 ? generationCountMax : Math.min(generationCountMax, n);
    setGenerationCount(String(maxGen));
    setCombinationCount(combinationCountMax.toString());
  }, [
    selectedColors.length,
    generationCountMax,
    combinationCountMax,
    setGenerationCount,
    setCombinationCount,
  ]);

  const toggleColorSelection = (color: string) => {
    if (selectedColors.includes(color)) {
      setSelectedColors(selectedColors.filter((c) => c !== color));
    } else {
      if (selectedColors.length >= maxSelection) {
        Alert.alert(
          'Maximum Reached',
          `You can select up to ${maxSelection} colors${!canExport ? '. Upgrade to Plus for more.' : ''}`
        );
        return;
      }
      setSelectedColors([...selectedColors, color]);
    }
  };

  const trackColorGeneratorUsage = async () => {
    try {
      const updatedUser = await usersAPI.trackBadgeUsage('color_generator');
      if (updatedUser) {
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
      }
    } catch {
      // Track usage failed silently
    }
  };

  const scrollToPaletteResults = () => {
    // Use longer timeout for tablets/web to ensure DOM has updated
    const timeout = isTablet ? 300 : 100;

    setTimeout(() => {
      if (Platform.OS === 'web' && paletteResultsRef.current) {
        try {
          const element =
            paletteResultsRef.current as unknown as HTMLElement | null;
          if (element && typeof element.scrollIntoView === 'function') {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (element && element.getBoundingClientRect) {
            const rect = element.getBoundingClientRect();
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;
            const targetY = rect.top + scrollTop - 20;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
          }
        } catch {
          // Scroll failed silently
        }
      } else if (
        isMobile &&
        paletteResultsRef.current &&
        scrollViewRef.current
      ) {
        // ScrollView has NativeMethods at runtime; double assertion needed for measureLayout typing
        paletteResultsRef.current.measureLayout(
          scrollViewRef.current as unknown as Parameters<
            View['measureLayout']
          >[0],
          (x: number, y: number) => {
            scrollViewRef.current?.scrollTo({ y: y - 20, animated: true });
          },
          () => {}
        );
      }
    }, timeout);
  };

  const generatePaletteFromSelection = () => {
    if (isPublicPreview && publicPreviewTryUsed === true) {
      navigation.navigate('Register');
      return;
    }

    trackButtonClick('Generate Palette', 'color-generator-screen', {
      button_type: 'generate_palette',
      screen: 'color-generator',
      selected_colors_count: selectedColors.length,
      generation_count: generationCount,
      combination_count: combinationCount,
    });

    if (selectedColors.length === 0) {
      Alert.alert('No Colors Selected', 'Please select at least one color');
      return;
    }

    const count = parseInt(generationCount, 10);
    if (isNaN(count) || count < 1 || count > generationCountMax) {
      Alert.alert(
        'Invalid Count',
        `Please enter a number between 1 and ${generationCountMax}`
      );
      return;
    }
    if (
      count > selectedColors.length &&
      (!canUseHarmony || harmonyMode === 'random')
    ) {
      Alert.alert(
        'Invalid Count',
        `Please enter a number between 1 and ${selectedColors.length} (you selected ${selectedColors.length} colors)`
      );
      return;
    }

    trackColorGeneratorUsage();

    const combinations = generateFromSelection(
      selectedColors,
      { generationCountMax, combinationCountMax },
      canUseHarmony
    );
    setMultipleCombinations(combinations);
    setGeneratedPalette(combinations[0]?.colors || []);
    setColorPalette(combinations[0]?.colors || []);
    setRandomPaletteName(null);
    setIsGenerating(true);
    scrollToPaletteResults();
    setTimeout(() => setIsGenerating(false), 400);
  };

  const generateRandomPalette = () => {
    trackButtonClick('Randomize', 'color-generator-screen', {
      button_type: 'randomize_palette',
      screen: 'color-generator',
      generation_count: generationCount,
      combination_count: combinationCount,
    });

    const result = generateRandom({
      generationCountMax,
      combinationCountMax,
    });
    setMultipleCombinations(result);
    setGeneratedPalette(result[0]?.colors || []);
    setColorPalette(result[0]?.colors || []);
    setRandomPaletteName(
      parseInt(combinationCount, 10) === 1 ? (result[0]?.name ?? null) : null
    );
    setSelectedPaletteIndex(null);
    setSelectedColors([]);

    scrollToPaletteResults();
  };

  const handleUseMyPalette = () => {
    if (isNavigating) return;
    if (recommendedColors.length > 0) {
      const colorsToAdd = recommendedColors.filter(
        (color) => !selectedColors.includes(color)
      );
      const remainingSlots = maxSelection - selectedColors.length;
      const colorsToSelect = colorsToAdd.slice(0, remainingSlots);
      if (colorsToSelect.length > 0) {
        setSelectedColors([...selectedColors, ...colorsToSelect]);
        showToastMessage(
          `Added ${colorsToSelect.length} color${colorsToSelect.length > 1 ? 's' : ''} from your palette`
        );
      } else if (remainingSlots === 0) {
        setTimeout(() => {
          Alert.alert(
            'Maximum Reached',
            `You can select up to ${maxSelection} colors`
          );
        }, 50);
      } else {
        setTimeout(() => {
          Alert.alert(
            'Already Selected',
            'All recommended colors are already selected'
          );
        }, 50);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in confirm/alert callbacks
      const goToColorAnalysis = () => {
        if (isNavigating) return;
        setIsNavigating(true);
        onClose?.();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in setTimeout
        const doNavigate = () => {
          if (onTabPress) {
            onTabPress('ColourAnalysis');
          } else {
            try {
              navigation.dispatch(
                CommonActions.navigate({ name: 'ColourAnalysisMobile' })
              );
            } catch {
              navigate('MainTabs', { section: 'color-analysis' });
            }
            setIsNavigating(false);
          }
        };
        setTimeout(doNavigate, 100);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const confirmed = window.confirm(
            "You don't have a recommended color palette yet. Would you like to generate one through color analysis?"
          );
          if (confirmed) goToColorAnalysis();
        } else {
          setTimeout(() => {
            Alert.alert(
              'No Palette Available',
              "You don't have a recommended color palette yet. Would you like to generate one through color analysis?",
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Okay', onPress: goToColorAnalysis },
              ],
              { cancelable: true }
            );
          }, 50);
        }
      };
    }
  };

  // Reserved for future use when palette swatch click is wired
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectColorFromPalette = (index: number) => {
    setSelectedPaletteIndex(index);
  };

  const removeColorFromPalette = (index: number) => {
    const newPalette = colorPalette.filter((_, i) => i !== index);
    setColorPalette(newPalette);
    setGeneratedPalette(newPalette);
    if (selectedPaletteIndex === index) {
      setSelectedPaletteIndex(null);
    } else if (selectedPaletteIndex !== null && selectedPaletteIndex > index) {
      setSelectedPaletteIndex(selectedPaletteIndex - 1);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        navigator.clipboard
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      } else if (Platform.OS !== 'web') {
        // For React Native, try to use @react-native-clipboard/clipboard if available
        // Note: webpack is configured to ignore this module for web builds
        try {
          // @ts-ignore - Native-only module, ignored by webpack for web builds
          const Clipboard =
            require('@react-native-clipboard/clipboard').default;
          if (Clipboard && Clipboard.setString) {
            Clipboard.setString(text);
            return true;
          }
          return false;
        } catch {
          // Fallback if package not available - just return false
          return false;
        }
      }
      return false;
    } catch {
      return false;
    }
  };

  const copyPalette = async () => {
    if (multipleCombinations.length > 0) {
      // Copy all combinations
      const allCombinationsText = multipleCombinations
        .map((combo, index) => {
          if (combo.name) {
            return `${combo.name}: ${combo.colors.join(', ')}`;
          }
          return `Combination ${index + 1}: ${combo.colors.join(', ')}`;
        })
        .join('\n\n');
      const success = await copyToClipboard(allCombinationsText);
      if (success) {
        showToastMessage('Palettes copied!');
      } else {
        Alert.alert('Copy Palettes', allCombinationsText);
      }
    } else {
      const paletteToCopy =
        generatedPalette.length > 0 ? generatedPalette : colorPalette;
      const paletteText = paletteToCopy.join(', ');
      const success = await copyToClipboard(paletteText);
      if (success) {
        showToastMessage('Palette copied!');
      } else {
        Alert.alert('Copy Palette', paletteText);
      }
    }
  };

  const copyPaletteAtIndex = async (index: number) => {
    const palettes =
      multipleCombinations.length > 0
        ? multipleCombinations
        : [
            {
              colors:
                generatedPalette.length > 0 ? generatedPalette : colorPalette,
            },
          ];
    const palette = palettes[index];
    if (!palette?.colors?.length) return;
    const name =
      'name' in palette && palette.name ? palette.name : `Palette ${index + 1}`;
    const text = `${name}: ${palette.colors.join(', ')}`;
    const success = await copyToClipboard(text);
    if (success) {
      showToastMessage('Palette copied!');
    } else {
      Alert.alert('Copy Palette', text);
    }
  };

  // Copy individual color
  const copyColor = async (color: string) => {
    const success = await copyToClipboard(color);
    if (success) {
      showToastMessage('Copied!');
    } else {
      Alert.alert('Copy Color', color);
    }
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    Animated.timing(toastAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setTimeout(() => {
        Animated.timing(toastAnim, {
          toValue: 100,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setShowToast(false);
        });
      }, 2000);
    });
  };

  // Reset toast animation when component mounts
  useEffect(() => {
    toastAnim.setValue(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toastAnim ref is stable, init once
  }, []);

  useEffect(() => {
    if (isPublicPreview) return;

    const fetchRecommendedColors = async () => {
      try {
        setLoadingRecommended(true);
        const analysis = await getRecentAnalysis();
        if (
          analysis.recommended_colors &&
          analysis.recommended_colors.length > 0
        ) {
          setRecommendedColors(analysis.recommended_colors);
        } else if (
          analysis.dominant_colours &&
          analysis.dominant_colours.length > 0
        ) {
          // Fallback to dominant colors if no recommended colors
          const colors = analysis.dominant_colours
            .map((c: { hex?: string } | string) =>
              typeof c === 'object' && c && 'hex' in c ? c.hex : c
            )
            .filter((s): s is string => typeof s === 'string');
          setRecommendedColors(colors);
        } else {
          // No colors found in analysis - ensure empty array
          setRecommendedColors([]);
        }
      } catch {
        setRecommendedColors([]);
      } finally {
        setLoadingRecommended(false);
      }
    };

    fetchRecommendedColors();
  }, [isPublicPreview]);

  // Update screen width on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });

    return () => subscription?.remove();
  }, []);

  // Derived palettes for PaletteResult (single flow: selector top, result bottom)
  const palettes = useMemo(() => {
    if (multipleCombinations.length > 0) return multipleCombinations;
    const colors =
      generatedPalette.length > 0 ? generatedPalette : colorPalette;
    return colors.length > 0 ? [{ colors }] : [];
  }, [multipleCombinations, generatedPalette, colorPalette]);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View
        style={[styles.boxedContainer, isMobile && styles.boxedContainerMobile]}
      >
        {/* Search at top - full width */}
        {!isPublicPreview && (
          <View style={styles.topSearchWrap}>
            <SearchBar
              value={generatorSearchQuery}
              onChangeText={setGeneratorSearchQuery}
              placeholder="Search colours or hex"
              showClearButton
              onClear={() => setGeneratorSearchQuery('')}
              size="wide"
              containerStyle={styles.topSearchContainer}
            />
          </View>
        )}
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollViewMobile}
          contentContainerStyle={[
            styles.scrollViewContentMobile,
            { paddingBottom: scrollViewBottomPadding || 220 },
          ]}
          showsVerticalScrollIndicator={false}
          bounces={true}
          scrollEventThrottle={16}
          nestedScrollEnabled={false}
        >
          {!isPublicPreview && (
            <PageHero
              title="Palette Generator"
              subtitle="Pick colours, use your palette, or randomize. Generate harmonies and copy hex codes."
            />
          )}
          <View
            style={[
              styles.boxContent,
              { backgroundColor: colors.card },
              isMobile && styles.boxContentMobile,
            ]}
          >
            {/* Colour selector — boxed, pills + grid, ~5 rows visible */}
            {isPublicPreview ? (
              <BlurredLockOverlay
                message="Sign up to select your own colors!"
                blurAmount={5}
                overlayOpacity={0.55}
                style={styles.leftPanelBlurred}
              >
                <ColorPicker
                  selectedColors={selectedColors}
                  onColorToggle={() => {}}
                  maxSelection={10}
                />
              </BlurredLockOverlay>
            ) : (
              <View
                accessibilityLabel="Colour selector"
                accessibilityRole="region"
              >
                <ColorPicker
                  selectedColors={selectedColors}
                  onColorToggle={toggleColorSelection}
                  maxSelection={maxSelection}
                  searchQuery={generatorSearchQuery}
                  onSearchQueryChange={setGeneratorSearchQuery}
                  maxVisibleRows={5}
                />
              </View>
            )}
            {/* Your colours — boxed section (same style as selector) per redesign spec */}
            {!isPublicPreview && (
              <SelectedColorsStrip
                selectedColors={selectedColors}
                maxSelection={maxSelection}
                onRemoveColor={toggleColorSelection}
                onClear={() => setSelectedColors([])}
                boxed
              />
            )}
            <CreatePalettesCard
              isPublicPreview={isPublicPreview}
              canUseHarmony={canUseHarmony}
              hideUseMyPalette={hideUseMyPalette}
              recommendedColorsCount={recommendedColors.length}
              selectedColorsLength={selectedColors.length}
              maxSelection={maxSelection}
              generationCount={generationCount}
              setGenerationCount={setGenerationCount}
              combinationCount={combinationCount}
              setCombinationCount={setCombinationCount}
              generationCountMax={generationCountMax}
              combinationCountMax={combinationCountMax}
              harmonyMode={harmonyMode}
              setHarmonyMode={setHarmonyMode}
              onUseMyPalette={handleUseMyPalette}
              onRandomize={generateRandomPalette}
              onGenerate={generatePaletteFromSelection}
              isGenerating={isGenerating}
              isMobile={isMobile}
              isTablet={isTablet}
            />

            <View ref={paletteResultsRef} style={styles.paletteResultWrap}>
              <PaletteResult
                palettes={palettes}
                onCopyPalette={copyPalette}
                onCopyPaletteAtIndex={copyPaletteAtIndex}
                onCopyCss={async () => {
                  const pal =
                    multipleCombinations.length > 0
                      ? multipleCombinations[0].colors
                      : generatedPalette.length > 0
                        ? generatedPalette
                        : colorPalette;
                  if (pal.length === 0) return;
                  const cssVars = pal
                    .map((c, i) => `  --color-${i + 1}: ${c};`)
                    .join('\n');
                  const ok = await copyToClipboard(`:root {\n${cssVars}\n}`);
                  if (ok) showToastMessage('CSS variables copied!');
                }}
                onCopyColor={copyColor}
                canExport={canExport}
                selectedPaletteIndex={selectedPaletteIndex}
                onSelectPaletteIndex={setSelectedPaletteIndex}
                onRemoveColor={
                  palettes.length === 1
                    ? (_, idx) => removeColorFromPalette(idx)
                    : undefined
                }
                isPublicPreview={isPublicPreview}
                onExportLockPress={() => navigation.navigate('Pricing')}
              />
            </View>
          </View>
          {/* Dynamic spacer for bottom navigation - adjusts based on content */}
          {isMobile && <View style={{ height: 0 }} />}
        </ScrollView>
      </View>
      {/* Toast Notification - Bottom Right */}
      {showToast && (
        <Animated.View
          style={[
            styles.toastContainer,
            {
              transform: [{ translateY: toastAnim }],
            },
          ]}
        >
          <View
            style={[styles.toastContent, { backgroundColor: colors.primary }]}
          >
            <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
            <Text style={[styles.toastText, { color: '#FFFFFF' }]}>
              {toastMessage}
            </Text>
          </View>
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  topSearchWrap: {
    width: '100%',
    maxWidth: 920,
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
  },
  topSearchContainer: {
    width: '100%',
  },
  boxedContainer: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 0,
    paddingBottom: 16,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  boxedContainerMobile: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    justifyContent: 'flex-start',
  },
  boxContent: {
    width: '100%',
    maxWidth: 920,
    alignSelf: 'center',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  },
  boxContentMobile: {
    height: undefined,
    maxHeight: undefined,
    minHeight: 0,
    paddingHorizontal: 16,
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  scrollViewMobile: {
    flex: 1,
    width: '100%',
  },
  scrollViewContentMobile: {
    maxWidth: 920,
    alignSelf: 'center',
    width: '100%',
  },
  paletteResultWrap: {
    marginTop: 16,
    width: '100%',
  },
  leftPanelBlurred: {
    flex: 1,
    alignSelf: 'stretch',
    minHeight: 200,
  },
  toastContainer: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 20 : 20,
    right: 16,
    zIndex: 1000,
    alignItems: 'flex-end',
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    minWidth: 150,
    maxWidth: 300,
  },
  toastText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
});

export default ColorGenerator;
